import { c as _c } from "react/compiler-runtime";
// «Финансы» площадки: внешний контур (долг продавцам и перевозчикам, заявки на
// выплату), внутренний (доход площадки) и «Бухгалтерия» (выгрузки для 1С).
// Все числа считает шлюз (services/finance.ts, payouts.ts, bookkeeping.ts).
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calculator, FileText, Loader2, TrendingDown, TrendingUp, Users, Wallet } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Legend, XAxis, YAxis } from "recharts";
import { api, type AccountingDataset, type DashboardRange, type FinanceBreakdownRow, type FinanceExternal, type FinanceInternal, type FinanceObligation, type FinanceSummary, type Metric, type ObligationDocument, type PayoutSchedule } from "@/api";
import { useAdvanceObligation, useFinanceSummary, useGenerateObligationDocuments } from "@/api/hooks/use-dashboard";
import { AdminDetailModal } from "@/components/admin-detail-modal";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { RangePicker } from "@/components/range-picker";
import { Segmented } from "@/components/segmented";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { ShopDetailContent } from "./shop-detail";
import { useShopRoute } from "@/api/hooks/use-storefronts";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { CommissionsMovedNote } from "@/components/commissions-moved-note";
import { PayoutBoard } from "@/components/payout-board";
import { SplitPaymentsCard } from "@/components/split-payments-card";
import { AccountingCenter } from "@/components/accounting-center";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { axisProps, ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { formatCurrency, formatDateish, formatNumber } from "@osmix/formatters";
import { cn } from "@/lib/utils";

/** Which breakdown a clicked row came from — decides the modal's deep link. */
type BreakdownKind = "byCategory" | "byShop";
type SelectedRow = {
  kind: BreakdownKind;
  row: FinanceBreakdownRow;
};
const shortDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", {
  day: "2-digit",
  month: "short"
});
const money = (value: number) => formatCurrency(value, {
  maximumFractionDigits: 0
});

/** Подпись оси: тысячи — «12k», мелкие суммы — как есть, без «0,004k». */
const axisMoney = (value: number) => Math.abs(value) >= 1000 ? `${formatNumber(Math.round(value / 100) / 10)}k` : formatNumber(Math.round(value));

/** One labelled figure inside a modal. */
function DetailStat(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-xs text-muted-foreground">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function StatCard(t0) {
  const $ = _c(21);
  const {
    title,
    metric,
    invented: t1
  } = t0;
  const invented = t1 === undefined ? false : t1;
  const up = (metric.delta ?? 0) >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  let t2;
  if ($[0] !== title) {
    t2 = <CardTitle className="text-sm font-medium">{title}</CardTitle>;
    $[0] = title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== invented) {
    t3 = invented && <Badge variant="outline" className="text-[10px] uppercase">mock</Badge>;
    $[2] = invented;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">{t2}{t3}</CardHeader>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== metric.value) {
    t5 = money(metric.value);
    $[7] = metric.value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <div className="text-2xl font-bold tabular-nums">{t5}</div>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== Icon || $[12] !== metric.delta || $[13] !== up) {
    t7 = metric.delta !== undefined && <span className={cn("mt-1 inline-flex items-center gap-1 text-xs font-medium", up ? "text-emerald-600 dark:text-emerald-500" : "text-destructive")}><Icon className="size-3" />{up ? "+" : ""}{(metric.delta * 100).toFixed(1)}%</span>;
    $[11] = Icon;
    $[12] = metric.delta;
    $[13] = up;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <CardContent>{t6}{t7}</CardContent>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t8) {
    t9 = <Card>{t4}{t8}</Card>;
    $[18] = t4;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}

/**
 * Settlement planning — «вот сегодня 10-е, к выводу там миллион триста».
 * Дата — открытие окна выплаты по графику продавцов (payoutSchedule.ts в
 * шлюзе): за 1-15 число с 25-го, за 16-конец месяца с 10-го следующего. До
 * 17.10.2026 здесь стояли даты 14-дневной сетки и не совпадали с кабинетом.
 */
function PayoutScheduleCard(t0) {
  const $ = _c(51);
  const {
    schedule
  } = t0;
  const {
    t
  } = useTranslation();
  const days = Math.max(0, Math.ceil((new Date(schedule.nextDate).getTime() - Date.now()) / 86400000));
  let t1;
  if ($[0] !== t) {
    t1 = t("finances.schedule.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <CardHeader className="pb-2"><CardTitle className="text-base">{t1}</CardTitle></CardHeader>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== schedule.nextDate || $[5] !== t) {
    t3 = t("finances.schedule.due", {
      date: formatDateish(schedule.nextDate)
    });
    $[4] = schedule.nextDate;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = schedule.periodLabel ? ` ${t("finances.schedule.forPeriod", {
    period: schedule.periodLabel
  })}` : "";
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <span className="text-sm text-muted-foreground">{t3}{t4}</span>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== schedule.amountDue) {
    t6 = formatCurrency(schedule.amountDue, {
      maximumFractionDigits: 0
    });
    $[10] = schedule.amountDue;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <span className="text-3xl font-bold tabular-nums">{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== schedule.sellersCount || $[15] !== t) {
    t8 = t("finances.schedule.sellers", {
      count: schedule.sellersCount
    });
    $[14] = schedule.sellersCount;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== days || $[18] !== t) {
    t9 = t("finances.schedule.inDays", {
      count: days
    });
    $[17] = days;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <span className="text-xs text-muted-foreground">{t8} ·{" "}{t9}</span>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== schedule.availableNow || $[24] !== t) {
    t11 = schedule.availableNow != null && schedule.availableNow > 0 && <span className="text-xs font-medium" data-testid="schedule-available-now">{t("finances.schedule.availableNow", {
        amount: formatCurrency(schedule.availableNow, {
          maximumFractionDigits: 0
        })
      })}</span>;
    $[23] = schedule.availableNow;
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("finances.schedule.rule");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <span className="max-w-sm text-xs text-muted-foreground">{t12}</span>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t13 || $[33] !== t5 || $[34] !== t7) {
    t14 = <div className="flex flex-col gap-1">{t5}{t7}{t10}{t11}{t13}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t13;
    $[33] = t5;
    $[34] = t7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("finances.schedule.history");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15) {
    t16 = <span className="text-xs uppercase tracking-wider text-muted-foreground">{t15}</span>;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== schedule.history) {
    t17 = schedule.history.map(_temp);
    $[40] = schedule.history;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t16 || $[43] !== t17) {
    t18 = <div className="flex flex-col gap-1">{t16}{t17}</div>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t14 || $[46] !== t18) {
    t19 = <CardContent className="flex flex-wrap items-end justify-between gap-6">{t14}{t18}</CardContent>;
    $[45] = t14;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t19 || $[49] !== t2) {
    t20 = <Card>{t2}{t19}</Card>;
    $[48] = t19;
    $[49] = t2;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
}

/** One line of a money breakdown: label, amount, optional emphasis. */
function _temp(entry) {
  return <div key={entry.date} className="flex items-baseline justify-between gap-6 text-sm"><span className="text-muted-foreground">{formatDateish(entry.date)}</span><span className="tabular-nums">{formatCurrency(entry.amount, {
        maximumFractionDigits: 0
      })}</span></div>;
}
function MoneyRow(t0) {
  const $ = _c(22);
  const {
    label,
    value,
    hint,
    strong: t1,
    tone
  } = t0;
  const strong = t1 === undefined ? false : t1;
  const t2 = strong ? "font-medium" : "text-muted-foreground";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("text-sm", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label || $[3] !== t3) {
    t4 = <span className={t3}>{label}</span>;
    $[2] = label;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== hint) {
    t5 = hint && <span className="text-xs text-muted-foreground">{hint}</span>;
    $[5] = hint;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex flex-col">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = strong ? "text-lg font-semibold" : "text-sm";
  const t8 = tone === "danger" && "text-destructive";
  const t9 = tone === "muted" && "text-muted-foreground";
  let t10;
  if ($[10] !== t7 || $[11] !== t8 || $[12] !== t9) {
    t10 = cn("tabular-nums", t7, t8, t9);
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== value) {
    t11 = money(value);
    $[14] = value;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t6) {
    t13 = <div className="flex items-baseline justify-between gap-4 border-b py-2 last:border-0">{t6}{t12}</div>;
    $[19] = t12;
    $[20] = t6;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
}

/**
 * EXTERNAL CONTOUR. Money owed to sellers, split into accrued / blocked /
 * payable, plus delivery shown as the transit money it is. The founder read the
 * old single "к выводу" figure and could not tell which of the three it was.
 */
function ExternalContourCard(t0) {
  const $ = _c(79);
  const {
    external
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("finances.external.sellersTitle");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <CardHeader className="pb-2"><CardTitle className="text-base">{t1}</CardTitle></CardHeader>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("finances.external.turnover");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== external.gmv || $[7] !== t3) {
    t4 = <MoneyRow label={t3} value={external.gmv} strong={true} />;
    $[6] = external.gmv;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("finances.external.accrued");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== external.accrued || $[12] !== t5) {
    t6 = <MoneyRow label={t5} value={external.accrued} />;
    $[11] = external.accrued;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("finances.external.blocked");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = external.blocked;
  let t9;
  if ($[16] !== t) {
    t9 = t("finances.external.blockedHint");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== external.blocked || $[19] !== t7 || $[20] !== t9) {
    t10 = <MoneyRow label={t7} value={t8} hint={t9} tone="muted" />;
    $[18] = external.blocked;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("finances.external.available");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== external.available || $[25] !== t11) {
    t12 = <MoneyRow label={t11} value={external.available} strong={true} />;
    $[24] = external.available;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== external.pending || $[28] !== t) {
    t13 = external.pending != null && external.pending > 0 && <MoneyRow label={t("finances.external.pending")} value={external.pending} tone="muted" />;
    $[27] = external.pending;
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("finances.external.paid");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== external.paid || $[33] !== t14) {
    t15 = <MoneyRow label={t14} value={external.paid} tone="muted" />;
    $[32] = external.paid;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t10 || $[36] !== t12 || $[37] !== t13 || $[38] !== t15 || $[39] !== t4 || $[40] !== t6) {
    t16 = <CardContent className="flex flex-col">{t4}{t6}{t10}{t12}{t13}{t15}</CardContent>;
    $[35] = t10;
    $[36] = t12;
    $[37] = t13;
    $[38] = t15;
    $[39] = t4;
    $[40] = t6;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t16 || $[43] !== t2) {
    t17 = <Card>{t2}{t16}</Card>;
    $[42] = t16;
    $[43] = t2;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t) {
    t18 = t("finances.external.deliveryTitle");
    $[45] = t;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t18) {
    t19 = <CardHeader className="pb-2"><CardTitle className="text-base">{t18}</CardTitle></CardHeader>;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t) {
    t20 = t("finances.external.deliveryHint");
    $[49] = t;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t20) {
    t21 = <p className="pb-2 text-xs text-muted-foreground">{t20}</p>;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t) {
    t22 = t("finances.external.withheld");
    $[53] = t;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== external.delivery.withheld || $[56] !== t22) {
    t23 = <MoneyRow label={t22} value={external.delivery.withheld} />;
    $[55] = external.delivery.withheld;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t) {
    t24 = t("finances.external.carrierPayable");
    $[58] = t;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== external.delivery.payable || $[61] !== t24) {
    t25 = <MoneyRow label={t24} value={external.delivery.payable} strong={true} />;
    $[60] = external.delivery.payable;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t) {
    t26 = t("finances.external.carrierPaid");
    $[63] = t;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== external.delivery.paid || $[66] !== t26) {
    t27 = <MoneyRow label={t26} value={external.delivery.paid} tone="muted" />;
    $[65] = external.delivery.paid;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t21 || $[69] !== t23 || $[70] !== t25 || $[71] !== t27) {
    t28 = <CardContent className="flex flex-col">{t21}{t23}{t25}{t27}</CardContent>;
    $[68] = t21;
    $[69] = t23;
    $[70] = t25;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t19 || $[74] !== t28) {
    t29 = <Card>{t19}{t28}</Card>;
    $[73] = t19;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t17 || $[77] !== t29) {
    t30 = <div className="grid gap-4 xl:grid-cols-2">{t17}{t29}</div>;
    $[76] = t17;
    $[77] = t29;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  return t30;
}
const DOC_DATASET: Record<string, AccountingDataset> = {
  "agent-report": "agent-reports",
  "commission-act": "commission-acts"
};
const monthName = (period: string) => {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)).toLocaleDateString("ru-RU", {
    month: "short",
    year: "numeric"
  });
};

/**
 * Документ обязательства открывается реальной выгрузкой «Бухгалтерии»: отчёт
 * агента — печатной формой во вкладке, акт вознаграждения — xlsx. Старые
 * пустые записи (без месяца) честно подписаны как «без файла».
 */
function ObligationDocuments({
  documents,
  canDownload
}: {
  documents: ObligationDocument[];
  canDownload: boolean;
}) {
  const {
    t
  } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = async (doc: ObligationDocument) => {
    const dataset = DOC_DATASET[doc.kind];
    if (!dataset || !doc.period) return;
    setBusy(doc.id);
    setError(null);
    try {
      const format = doc.kind === "agent-report" ? "html" : "xlsx";
      const file = await api.finances.exportAccounting(dataset, {
        period: doc.period,
        shopId: doc.shopId
      }, format);
      const url = URL.createObjectURL(format === "html" ? new Blob([file.blob], {
        type: "text/html;charset=utf-8"
      }) : file.blob);
      if (format === "html") {
        const tab = window.open(url, "_blank");
        if (!tab) window.location.assign(url);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  if (documents.length === 0) return null;
  return <div className="flex flex-col items-start gap-0.5">
			{documents.map(doc_0 => {
      const real = Boolean(DOC_DATASET[doc_0.kind] && doc_0.period);
      const label = real ? `${t(`finances.external.doc.${doc_0.kind}`)} · ${monthName(doc_0.period ?? "")}` : t("finances.external.doc.legacy");
      return real && canDownload ? <button key={doc_0.id} type="button" className="inline-flex items-center gap-1 text-left text-xs text-primary underline-offset-2 hover:underline disabled:opacity-60" disabled={busy !== null} onClick={() => void open(doc_0)}>
						{busy === doc_0.id ? <Loader2 className="size-3 animate-spin" /> : <FileText className="size-3" />}
						{label}
					</button> : <span key={doc_0.id} className="text-xs text-muted-foreground">
						{label}
					</span>;
    })}
			{error && <span className="text-xs text-destructive">{error}</span>}
		</div>;
}

/**
 * The obligations ledger — debit and credit per counterparty. This is the table
 * that turns the screen from a report into a tool: «надо, чтобы финансовый
 * дашборд показывал не аналитику, а помогал делать выплаты».
 *
 * С 17.10.2026 сетка одна: обязательства идут по периодам выплат продавцам
 * (половины месяца), начислено — те же числа, что выписка продавца, выплачено —
 * его выплаченные заявки за период. Выплаченная заявка закрывает обязательство
 * сама; у продавца вместо кнопки «Оплачено» — его заявки по периоду. Отметка
 * «Оплачено» осталась только перевозчику.
 */
function ObligationsCard(t0) {
  const $ = _c(72);
  const {
    obligations,
    legacyArchive,
    range,
    canManage,
    canDownload
  } = t0;
  const {
    t
  } = useTranslation();
  const advance = useAdvanceObligation(range);
  const documents = useGenerateObligationDocuments(range);
  const busy = advance.isPending || documents.isPending;
  const [confirmPaid, setConfirmPaid] = useState(null);
  let T0;
  let T1;
  let error;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== advance.error || $[1] !== documents.error || $[2] !== obligations || $[3] !== t) {
    const period = obligations.find(_temp2);
    error = (documents.error ?? advance.error) as Error | null;
    T1 = Card;
    t5 = "finances-obligations";
    T0 = CardHeader;
    t3 = "gap-1";
    let t6;
    if ($[12] !== t) {
      t6 = t("finances.external.obligations");
      $[12] = t;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    if ($[14] !== t6) {
      t4 = <CardTitle>{t6}</CardTitle>;
      $[14] = t6;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t1 = "text-sm text-muted-foreground";
    t2 = period ? t("finances.external.periodNote", {
      from: formatDateish(period.periodFrom ?? ""),
      to: formatDateish(period.periodTo ?? "")
    }) : t("finances.external.periodNoteEmpty");
    $[0] = advance.error;
    $[1] = documents.error;
    $[2] = obligations;
    $[3] = t;
    $[4] = T0;
    $[5] = T1;
    $[6] = error;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    T0 = $[4];
    T1 = $[5];
    error = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[16] !== t1 || $[17] !== t2) {
    t6 = <p className={t1}>{t2}</p>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t) {
    t7 = t("finances.external.cadenceWarning");
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <p className="text-xs text-muted-foreground">{t7}</p>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== legacyArchive || $[24] !== t) {
    t9 = legacyArchive && legacyArchive.obligations > 0 && <p className="text-xs text-muted-foreground" data-testid="obligations-legacy-archive">{t("finances.external.legacyArchive", {
        count: legacyArchive.obligations,
        amount: money(legacyArchive.paidMarked)
      })}</p>;
    $[23] = legacyArchive;
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== T0 || $[27] !== t3 || $[28] !== t4 || $[29] !== t6 || $[30] !== t8 || $[31] !== t9) {
    t10 = <T0 className={t3}>{t4}{t6}{t8}{t9}</T0>;
    $[26] = T0;
    $[27] = t3;
    $[28] = t4;
    $[29] = t6;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== error) {
    t11 = error && <p className="mb-2 text-sm text-destructive">{error.message}</p>;
    $[33] = error;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== advance || $[36] !== busy || $[37] !== canDownload || $[38] !== canManage || $[39] !== documents || $[40] !== obligations || $[41] !== t) {
    t12 = obligations.length === 0 ? <p className="text-sm text-muted-foreground">{t("finances.external.obligationsEmpty")}</p> : <Table><TableHeader><TableRow><TableHead>{t("finances.name")}</TableHead><TableHead className="text-right">{t("finances.external.col.accrued")}</TableHead><TableHead className="text-right">{t("finances.external.col.paid")}</TableHead><TableHead className="text-right">{t("finances.external.due")}</TableHead><TableHead>{t("finances.external.col.nextDate")}</TableHead><TableHead>{t("finances.external.state")}</TableHead><TableHead>{t("finances.external.documents")}</TableHead><TableHead className="text-right">{t("finances.external.action")}</TableHead></TableRow></TableHeader><TableBody>{obligations.map(row_0 => {
          const open = row_0.state !== "paid" && row_0.state !== "settled";
          const hasRealDocs = row_0.documents.some(_temp3);
          return <TableRow key={`${row_0.kind}_${row_0.key}`}><TableCell><div className="flex items-center gap-2"><span className="font-medium">{row_0.label}</span><Badge variant="outline" className="text-[10px]">{t(`finances.external.kind.${row_0.kind}`)}</Badge></div><span className="block text-xs text-muted-foreground">{row_0.periodLabel && row_0.kind === "seller" ? t("finances.external.periodCell", {
                  period: row_0.periodLabel,
                  date: formatDateish(row_0.opensAt ?? row_0.nextDate)
                }) : [row_0.periodLabel, t(`finances.external.cadenceValue.${row_0.cadence}`)].filter(Boolean).join(" \xB7 ")}</span>{row_0.kind === "seller" && (row_0.requests?.length ?? 0) > 0 && <span className="block text-xs" data-testid="obligation-requests">{t("finances.external.requests")}:{" "}{(row_0.requests ?? []).map(r => t("finances.external.requestLine", {
                  amount: money(r.amount),
                  status: t(`finances.external.requestStatus.${r.status}`)
                })).join("; ")}</span>}</TableCell><TableCell className="text-right tabular-nums">{money(row_0.accrued)}</TableCell><TableCell className="text-right tabular-nums text-muted-foreground">{money(row_0.paid)}</TableCell><TableCell className="text-right font-semibold tabular-nums">{money(row_0.due)}</TableCell><TableCell className="text-sm text-muted-foreground">{formatDateish(row_0.nextDate)}</TableCell><TableCell><Badge variant={row_0.state === "paid" || row_0.state === "settled" ? "default" : "outline"} className="text-[10px] whitespace-nowrap">{t(`finances.external.stateValue.${row_0.state}`)}</Badge></TableCell><TableCell className="min-w-44 whitespace-normal">{row_0.kind === "carrier" ? <span className="text-xs text-muted-foreground">{t("finances.external.doc.carrier")}</span> : <ObligationDocuments documents={row_0.documents} canDownload={canDownload} />}</TableCell><TableCell className="text-right"><div className="flex justify-end gap-2">{canManage && row_0.kind === "seller" && !hasRealDocs && open && <Button size="sm" variant="outline" disabled={busy} onClick={() => documents.mutate(row_0.key)}>{t("finances.external.generateDocuments")}</Button>}{canManage && open && row_0.due > 0 && row_0.kind === "carrier" && <Button size="sm" disabled={busy} onClick={() => setConfirmPaid(row_0)}>{t("finances.external.markPaid")}</Button>}{open && row_0.due > 0 && row_0.kind === "seller" && <span className="text-xs text-muted-foreground">{t("finances.external.closesByRequest")}</span>}{canManage && row_0.state === "paid" && <Button size="sm" variant="outline" disabled={busy} onClick={() => advance.mutate({
                  id: row_0.key,
                  state: "settled"
                })}>{t("finances.external.markSettled")}</Button>}</div></TableCell></TableRow>;
        })}</TableBody></Table>;
    $[35] = advance;
    $[36] = busy;
    $[37] = canDownload;
    $[38] = canManage;
    $[39] = documents;
    $[40] = obligations;
    $[41] = t;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t11 || $[44] !== t12) {
    t13 = <CardContent className="overflow-x-auto">{t11}{t12}</CardContent>;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const t14 = confirmPaid !== null;
  const t15 = confirmPaid?.label ?? "";
  let t16;
  if ($[46] !== t || $[47] !== t15) {
    t16 = t("finances.external.confirmPaidTitle", {
      name: t15
    });
    $[46] = t;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const t17 = confirmPaid?.kind === "carrier" ? "finances.external.confirmPaidCarrierText" : "finances.external.confirmPaidText";
  const t18 = confirmPaid?.due ?? 0;
  let t19;
  if ($[49] !== t || $[50] !== t17 || $[51] !== t18) {
    t19 = t(t17, {
      amount: money(t18)
    });
    $[49] = t;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t) {
    t20 = t("finances.external.markPaid");
    $[53] = t;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setConfirmPaid(null);
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== advance || $[57] !== confirmPaid) {
    t22 = () => {
      if (!confirmPaid) {
        return;
      }
      advance.mutate({
        id: confirmPaid.key,
        state: "paid"
      }, {
        onSettled: () => setConfirmPaid(null)
      });
    };
    $[56] = advance;
    $[57] = confirmPaid;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== advance.isPending || $[60] !== t14 || $[61] !== t16 || $[62] !== t19 || $[63] !== t20 || $[64] !== t22) {
    t23 = <ConfirmDialog open={t14} title={t16} description={t19} confirmLabel={t20} busy={advance.isPending} onClose={t21} onConfirm={t22} />;
    $[59] = advance.isPending;
    $[60] = t14;
    $[61] = t16;
    $[62] = t19;
    $[63] = t20;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== T1 || $[67] !== t10 || $[68] !== t13 || $[69] !== t23 || $[70] !== t5) {
    t24 = <T1 data-testid={t5}>{t10}{t13}{t23}</T1>;
    $[66] = T1;
    $[67] = t10;
    $[68] = t13;
    $[69] = t23;
    $[70] = t5;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  return t24;
}

/**
 * INTERNAL CONTOUR. What the platform earned — commission and advertising.
 *
 * Costs are deliberately NOT here. Nobody enters them into the platform and
 * nothing feeds them: «то, что касается каких-нибудь расходов… это не совсем
 * уже продуктовая логика», «от Excel-таблиц мы никуда не уйдём». A cost figure
 * the console cannot source is a figure it should not print.
 */
function _temp3(doc) {
  return DOC_DATASET[doc.kind] && doc.period;
}
function _temp2(row) {
  return row.periodFrom && row.periodTo;
}
function InternalContourCard(t0) {
  const $ = _c(26);
  const {
    internal
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("finances.internal.incomeTitle");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <CardHeader className="pb-2"><CardTitle className="text-base">{t1}</CardTitle></CardHeader>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("finances.commission");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== internal.commission || $[7] !== t3) {
    t4 = <MoneyRow label={t3} value={internal.commission} />;
    $[6] = internal.commission;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("finances.advertising");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== internal.advertising || $[12] !== t5) {
    t6 = <MoneyRow label={t5} value={internal.advertising} />;
    $[11] = internal.advertising;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("finances.internal.income");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== internal.income || $[17] !== t7) {
    t8 = <MoneyRow label={t7} value={internal.income} strong={true} />;
    $[16] = internal.income;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t6 || $[21] !== t8) {
    t9 = <CardContent className="flex flex-col">{t4}{t6}{t8}</CardContent>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t2 || $[24] !== t9) {
    t10 = <div className="grid gap-4 xl:grid-cols-2"><Card>{t2}{t9}</Card></div>;
    $[23] = t2;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
export function FinancesPage() {
  const $ = _c(95);
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      kind: "preset",
      days: 30
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [range, setRange] = useState(t0);
  let t1;
  if ($[1] !== user) {
    t1 = () => hasAdminPermission(user, "action.finances.manage") ? "external" : "accounting";
    $[1] = user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [contour, setContour] = useState(t1);
  const [selectedRow, setSelectedRow] = useState(null);
  const {
    data,
    error
  } = useFinanceSummary(range);
  if (error) {
    let t2;
    if ($[3] !== error.message) {
      t2 = <p className="text-destructive">{error.message}</p>;
      $[3] = error.message;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!data) {
    let t2;
    if ($[5] !== t) {
      t2 = t("common.loading");
      $[5] = t;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t2) {
      t3 = <p className="text-muted-foreground">{t2}</p>;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t2;
  if ($[9] !== t) {
    t2 = t("finances.gmv");
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = {
      label: t2,
      color: "var(--chart-1, #6366f1)"
    };
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t) {
    t4 = t("finances.commission");
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t4) {
    t5 = {
      label: t4,
      color: "var(--chart-4, #f59e0b)"
    };
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t5) {
    t6 = {
      gmv: t3,
      commission: t5
    };
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const config = t6;
  let t7;
  if ($[20] !== data.commissionSeries || $[21] !== data.gmvSeries) {
    let t8;
    if ($[23] !== data.commissionSeries) {
      t8 = (point, i) => ({
        date: point.date,
        gmv: point.value,
        commission: data.commissionSeries[i]?.value ?? 0
      });
      $[23] = data.commissionSeries;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t7 = data.gmvSeries.map(t8);
    $[20] = data.commissionSeries;
    $[21] = data.gmvSeries;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const chartData = t7;
  let t8;
  if ($[25] !== t) {
    t8 = t("finances.title");
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t8) {
    t9 = <h1 className="text-2xl font-bold tracking-tight">{t8}</h1>;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t) {
    t10 = t("finances.description");
    $[29] = t;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10) {
    t11 = <p className="text-sm text-muted-foreground">{t10}</p>;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t9) {
    t12 = <div data-tour="finances-intro">{t9}{t11}</div>;
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== range) {
    t13 = <div data-tour="finances-range"><RangePicker value={range} onChange={setRange} /></div>;
    $[36] = range;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t12 || $[39] !== t13) {
    t14 = <div className="flex flex-wrap items-start justify-between gap-4">{t12}{t13}</div>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t("finances.contour.external");
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = {
      value: "external",
      label: t15,
      icon: Users
    };
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t) {
    t17 = t("finances.contour.internal");
    $[45] = t;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t17) {
    t18 = {
      value: "internal",
      label: t17,
      icon: Wallet
    };
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t) {
    t19 = t("finances.contour.accounting");
    $[49] = t;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t19) {
    t20 = {
      value: "accounting",
      label: t19,
      icon: Calculator
    };
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t16 || $[54] !== t18 || $[55] !== t20) {
    t21 = [t16, t18, t20];
    $[53] = t16;
    $[54] = t18;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== contour || $[58] !== t21) {
    t22 = <div data-tour="finances-contour" className="max-w-full self-start overflow-x-auto"><Segmented value={contour} onChange={setContour} options={t21} /></div>;
    $[57] = contour;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== contour || $[61] !== data.advertising || $[62] !== data.aov || $[63] !== data.commission || $[64] !== data.external || $[65] !== data.gmv || $[66] !== data.internal || $[67] !== data.refunds || $[68] !== data.schedule || $[69] !== range || $[70] !== t || $[71] !== user) {
    t23 = contour === "accounting" ? <AccountingCenter /> : contour === "external" ? <><PayoutScheduleCard schedule={data.schedule} /><ExternalContourCard external={data.external} /><ObligationsCard obligations={data.external.obligations} legacyArchive={data.external.legacyArchive} range={range} canManage={hasAdminPermission(user, "action.finances.manage")} canDownload={hasAdminPermission(user, "action.exports.run")} /><PayoutBoard /><SplitPaymentsCard canManage={hasAdminPermission(user, "action.finances.manage")} /><CommissionsMovedNote /><div className="grid gap-4 md:grid-cols-3"><StatCard title={t("finances.gmv")} metric={data.gmv} /><StatCard title={t("finances.aov")} metric={data.aov} /><StatCard title={t("finances.refunds")} metric={data.refunds} /></div></> : <><InternalContourCard internal={data.internal} /><div className="grid gap-4 md:grid-cols-2"><StatCard title={t("finances.commission")} metric={data.commission} /><StatCard title={t("finances.advertising")} metric={data.advertising} invented={true} /></div></>;
    $[60] = contour;
    $[61] = data.advertising;
    $[62] = data.aov;
    $[63] = data.commission;
    $[64] = data.external;
    $[65] = data.gmv;
    $[66] = data.internal;
    $[67] = data.refunds;
    $[68] = data.schedule;
    $[69] = range;
    $[70] = t;
    $[71] = user;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== chartData || $[74] !== config || $[75] !== contour || $[76] !== data || $[77] !== t) {
    t24 = contour === "external" && <><Card><CardHeader><CardTitle>{t("finances.revenueChart")}</CardTitle></CardHeader><CardContent><ChartContainer height={280}><AreaChart data={chartData} margin={{
              left: 8,
              right: 8
            }}><defs>{Object.entries(config).map(_temp5)}</defs><CartesianGrid vertical={false} stroke="var(--border)" /><XAxis dataKey="date" tickFormatter={shortDate} {...axisProps} /><YAxis tickFormatter={axisMoney} {...axisProps} /><ChartTooltip config={config} formatter={money} labelFormatter={shortDate} /><Legend formatter={value => config[value]?.label ?? value} /><Area type="monotone" dataKey="gmv" stroke="var(--chart-1, #6366f1)" strokeWidth={2} fill="url(#fin-gmv)" isAnimationActive={false} /><Area type="monotone" dataKey="commission" stroke="var(--chart-4, #f59e0b)" strokeWidth={2} fill="url(#fin-commission)" isAnimationActive={false} /></AreaChart></ChartContainer></CardContent></Card><div className="grid gap-6 xl:grid-cols-2">{([["byCategory", t("finances.byCategory")], ["byShop", t("finances.byShop")]] as const).map(t25 => {
          const [key_0, title] = t25;
          return <Card key={key_0} className="min-w-0"><CardHeader><CardTitle>{title}</CardTitle></CardHeader><CardContent><Table><TableHeader><TableRow><TableHead>{t("finances.name")}</TableHead><TableHead className="text-right">{t("finances.gmv")}</TableHead><TableHead className="text-right">{t("finances.commission")}</TableHead><TableHead className="text-right">{t("finances.orders")}</TableHead></TableRow></TableHeader><TableBody>{data[key_0].map(row => <TableRow key={row.key} className="cursor-pointer" onClick={() => setSelectedRow({
                    kind: key_0,
                    row
                  })}><TableCell className="font-medium">{row.label}</TableCell><TableCell className="text-right tabular-nums">{money(row.gmv)}</TableCell><TableCell className="text-right tabular-nums">{money(row.commission)}</TableCell><TableCell className="text-right tabular-nums">{formatNumber(row.orders)}</TableCell></TableRow>)}</TableBody></Table></CardContent></Card>;
        })}</div></>;
    $[73] = chartData;
    $[74] = config;
    $[75] = contour;
    $[76] = data;
    $[77] = t;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== user) {
    t25 = hasAdminPermission(user, "page.shops");
    $[79] = user;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== user) {
    t26 = hasAdminPermission(user, "page.categories");
    $[81] = user;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setSelectedRow(null);
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== data || $[85] !== selectedRow || $[86] !== t25 || $[87] !== t26) {
    t28 = <BreakdownModal selected={selectedRow} summary={data} canOpenShops={t25} canOpenCategories={t26} onClose={t27} />;
    $[84] = data;
    $[85] = selectedRow;
    $[86] = t25;
    $[87] = t26;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== t14 || $[90] !== t22 || $[91] !== t23 || $[92] !== t24 || $[93] !== t28) {
    t29 = <div className="flex flex-col gap-6">{t14}{t22}{t23}{t24}{t28}</div>;
    $[89] = t14;
    $[90] = t22;
    $[91] = t23;
    $[92] = t24;
    $[93] = t28;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  return t29;
}

/**
 * Detail for one category/shop row: headline figures plus that row's share of
 * daily GMV. The per-row series is derived by weighting the summary series —
 * the mock has no per-row history, and inventing an independent one would imply
 * a precision this prototype doesn't have.
 */
function _temp5(t0) {
  const [key, series] = t0;
  return <linearGradient key={key} id={`fin-${key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={series.color} stopOpacity={0.3} /><stop offset="100%" stopColor={series.color} stopOpacity={0} /></linearGradient>;
}
function _temp4(s) {
  return s.user;
}
function BreakdownModal(t0) {
  const $ = _c(75);
  const {
    selected,
    summary,
    canOpenShops,
    canOpenCategories,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const shopRoute = useShopRoute(selected?.kind === "byShop" ? selected.row.key : undefined);
  if (!selected) {
    return null;
  }
  const {
    kind,
    row
  } = selected;
  const isShop = kind === "byShop";
  const totalGmv = summary[kind].reduce(_temp6, 0);
  const share = totalGmv > 0 ? row.gmv / totalGmv : 0;
  const aov = row.orders > 0 ? row.gmv / row.orders : 0;
  let t1;
  if ($[0] !== share || $[1] !== summary.gmvSeries) {
    let t2;
    if ($[3] !== share) {
      t2 = point => ({
        date: point.date,
        value: Math.round(point.value * share)
      });
      $[3] = share;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = summary.gmvSeries.map(t2);
    $[0] = share;
    $[1] = summary.gmvSeries;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const series = t1;
  const allowed = isShop ? canOpenShops : canOpenCategories;
  const href = isShop ? shopRoute.href : "/categories";
  const t2 = row.label;
  const t3 = allowed ? href : undefined;
  const t4 = isShop ? "dashboard.drill.shops" : "dashboard.drill.products";
  let t5;
  if ($[5] !== t || $[6] !== t4) {
    t5 = t(t4);
    $[5] = t;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClose) {
    t6 = open => !open && onClose();
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("finances.gmv");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== row.gmv) {
    t8 = money(row.gmv);
    $[12] = row.gmv;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <DetailStat label={t7} value={t8} />;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("finances.commission");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== row.commission) {
    t11 = money(row.commission);
    $[19] = row.commission;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = <DetailStat label={t10} value={t11} />;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t("finances.orders");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== row.orders) {
    t14 = formatNumber(row.orders);
    $[26] = row.orders;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <DetailStat label={t13} value={t14} />;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t) {
    t16 = t("finances.aov");
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== aov) {
    t17 = money(aov);
    $[33] = aov;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16 || $[36] !== t17) {
    t18 = <DetailStat label={t16} value={t17} />;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t12 || $[39] !== t15 || $[40] !== t18 || $[41] !== t9) {
    t19 = <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{t9}{t12}{t15}{t18}</div>;
    $[38] = t12;
    $[39] = t15;
    $[40] = t18;
    $[41] = t9;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("finances.share");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20) {
    t21 = <p className="text-xs text-muted-foreground">{t20}</p>;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = `${Math.round(share * 100)}%`;
  let t23;
  if ($[47] !== t22) {
    t23 = <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{
        width: t22
      }} /></div>;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const t24 = share * 100;
  let t25;
  if ($[49] !== t24) {
    t25 = t24.toFixed(1);
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t25) {
    t26 = <span className="text-sm font-medium tabular-nums">{t25}%</span>;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t23 || $[54] !== t26) {
    t27 = <div className="mt-2 flex items-center gap-3">{t23}{t26}</div>;
    $[53] = t23;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t21 || $[57] !== t27) {
    t28 = <div className="rounded-lg border p-3">{t21}{t27}</div>;
    $[56] = t21;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== isShop || $[60] !== row.key || $[61] !== series || $[62] !== shopRoute || $[63] !== t) {
    t29 = isShop ? <ShopDetailContent slug={shopRoute.key} shopId={row.key} /> : <div><p className="mb-2 text-sm font-medium">{t("finances.dailyGmv")}</p><ChartContainer height={200}><AreaChart data={series} margin={{
          left: 8,
          right: 8
        }}><defs><linearGradient id="fin-detail" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--chart-1, #6366f1)" stopOpacity={0.3} /><stop offset="100%" stopColor="var(--chart-1, #6366f1)" stopOpacity={0} /></linearGradient></defs><CartesianGrid vertical={false} stroke="var(--border)" /><XAxis dataKey="date" tickFormatter={shortDate} {...axisProps} /><YAxis tickFormatter={axisMoney} {...axisProps} /><ChartTooltip config={{
            value: {
              label: t("finances.gmv"),
              color: "var(--chart-1, #6366f1)"
            }
          }} formatter={money} labelFormatter={shortDate} /><Area type="monotone" dataKey="value" stroke="var(--chart-1, #6366f1)" strokeWidth={2} fill="url(#fin-detail)" isAnimationActive={false} /></AreaChart></ChartContainer></div>;
    $[59] = isShop;
    $[60] = row.key;
    $[61] = series;
    $[62] = shopRoute;
    $[63] = t;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t19 || $[66] !== t28 || $[67] !== t29) {
    t30 = <div className="flex flex-col gap-4">{t19}{t28}{t29}</div>;
    $[65] = t19;
    $[66] = t28;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== row.label || $[70] !== t3 || $[71] !== t30 || $[72] !== t5 || $[73] !== t6) {
    t31 = <AdminDetailModal open={true} title={t2} fullPageHref={t3} fullPageLabel={t5} onOpenChange={t6}>{t30}</AdminDetailModal>;
    $[69] = row.label;
    $[70] = t3;
    $[71] = t30;
    $[72] = t5;
    $[73] = t6;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  return t31;
}
function _temp6(sum, entry) {
  return sum + entry.gmv;
}